class ThemeAccessibility extends HTMLElement {
  connectedCallback() {
    if (window.acsbJS || document.querySelector('script[src*="acsbapp.com/apps/app/dist/js/app.js"]')) return;

    const script = document.createElement('script');
    script.src = 'https://acsbapp.com/apps/app/dist/js/app.js';
    script.async = true;
    script.onload = () => {
      window.acsbJS.init({
        statementLink: '',
        footerHtml: '',
        hideMobile: true,
        hideTrigger: true,
        disableBgProcess: false,
        language: 'en',
        position: 'left',
        leadColor: '#1b1b1b',
        triggerColor: '#1b1b1b',
        triggerRadius: '50%',
        triggerPositionX: 'left',
        triggerPositionY: 'bottom',
        triggerIcon: 'people',
        triggerSize: 'medium',
        triggerOffsetX: 210,
        triggerOffsetY: 20,
        mobile: {
          triggerSize: 'small',
          triggerPositionX: 'left',
          triggerPositionY: 'bottom',
          triggerOffsetX: 10,
          triggerOffsetY: 70,
          triggerRadius: '50%',
        },
      });
    };
    document.head.appendChild(script);
  }
}

if (!customElements.get('theme-accessibility')) {
  customElements.define('theme-accessibility', ThemeAccessibility);
}

class ThemeAccordionGroup extends HTMLElement {
  #mobileMedia = window.matchMedia('(max-width: 749px)');

  connectedCallback() {
    this.addEventListener('toggle', this.#handleToggle, true);
    if (this.hasAttribute('data-mobile-collapsible')) {
      this.#mobileMedia.addEventListener('change', this.#syncResponsiveState);
      this.#syncResponsiveState();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('toggle', this.#handleToggle, true);
    this.#mobileMedia.removeEventListener('change', this.#syncResponsiveState);
  }

  #handleToggle = (event) => {
    const opened = event.target;
    const selector = this.dataset.detailsSelector || 'details';
    if (!(opened instanceof HTMLDetailsElement) || !opened.matches(selector)) return;
    if (this.hasAttribute('data-mobile-collapsible') && !this.#mobileMedia.matches) {
      if (!opened.open) opened.open = true;
      return;
    }
    if (!opened.open) return;

    this.querySelectorAll(`${selector}[open]`).forEach((details) => {
      if (details !== opened) details.removeAttribute('open');
    });
  };

  #syncResponsiveState = () => {
    const open = !this.#mobileMedia.matches;
    this.querySelectorAll('details').forEach((details) => {
      details.open = open;
      const summary = details.querySelector('summary');
      if (summary instanceof HTMLElement) summary.tabIndex = open ? -1 : 0;
    });
  };
}

if (!customElements.get('theme-accordion-group')) {
  customElements.define('theme-accordion-group', ThemeAccordionGroup);
}

class ThemeMenuTabs extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('pointerover', this.#handlePointerOver);
    this.addEventListener('keydown', this.#handleKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('pointerover', this.#handlePointerOver);
    this.removeEventListener('keydown', this.#handleKeydown);
  }

  #tabs() {
    return Array.from(this.querySelectorAll('[role="tab"]'));
  }

  #activate(tab, moveFocus = false) {
    const panelId = tab.getAttribute('aria-controls');
    if (!panelId) return;

    this.#tabs().forEach((candidate) => {
      const selected = candidate === tab;
      candidate.setAttribute('aria-selected', String(selected));
      candidate.tabIndex = selected ? 0 : -1;
      const candidatePanelId = candidate.getAttribute('aria-controls');
      if (candidatePanelId) {
        const candidatePanel = this.querySelector('#' + CSS.escape(candidatePanelId));
        if (candidatePanel) candidatePanel.hidden = !selected;
      }
    });

    if (moveFocus) tab.focus();
  }

  #handleClick = (event) => {
    const tab = event.target.closest('[role="tab"]');
    if (tab && this.contains(tab)) this.#activate(tab);
  };

  #handlePointerOver = (event) => {
    if (event.pointerType === 'touch') return;
    const tab = event.target.closest('[role="tab"]');
    if (tab && this.contains(tab)) this.#activate(tab);
  };

  #handleKeydown = (event) => {
    const tab = event.target.closest('[role="tab"]');
    if (!tab || !this.contains(tab)) return;

    const tabs = this.#tabs();
    const index = tabs.indexOf(tab);
    let nextIndex;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (index + 1) % tabs.length;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (index - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = tabs.length - 1;
    if (nextIndex === undefined) return;

    event.preventDefault();
    this.#activate(tabs[nextIndex], true);
  };
}

if (!customElements.get('theme-menu-tabs')) {
  customElements.define('theme-menu-tabs', ThemeMenuTabs);
}
